/*
 * @Description: 分享链接国家/语言参数（RES-3272）：URL 参数与国家上下文的互转；大区短码别名（RES-3316）
 * @Author: liushichuan
 */
import config from '@/config';
import { IConvertedRegionItem, ILanguageConfig } from '@/services/common/types';
import { ICountry } from '@/types/common';
import { getLocalCountryInfo, setLocalCountryInfo, setLocalLanguage } from '@/utils/localStorage';

/** URL 上的国家参数名（区别于 layout/init 官网入口与 MarketingKitsDetails 外链入口既有的 countryCode 参数） */
export const COUNTRY_URL_PARAM = 'country';
/** URL 上的语言参数名。不能叫 lang：MarketingKitsDetails 已把 lang 用作资料语言筛选的外链参数（值同为 en-US 形态，会误触筛选） */
export const LANG_URL_PARAM = 'language';

/** 大区（All Regions）/ 大区 Other 的 countryCode 形态：后端大区 id（长数字）或 id + other 后缀（容忍手拼大写，存储归一为小写） */
const REGION_CODE_PATTERN = /^(\d{6,})(other)?$/i;

/** ISO 3166-1 alpha-2 形态的国家码（config.countryList 静态清单未收录全部开通国家，如 FR/LU 只存在于后端大区列表） */
const ISO_CODE_PATTERN = /^[A-Za-z]{2}$/;

/**
 * 大区短码别名（RES-3316）：regionList 透出的 regionCode（整区 / All Regions 口径）或 restCode（Others 口径），
 * 如 ROE / RoAPAC / RoNA / AFR / LAC。短码均 ≥3 个字母，与 2 位 ISO 国家码通道无冲突；大小写不敏感。
 * 落地时大区列表尚未拉取，前端不做翻译：原值透传给接口（服务端 countryCode 解析器识别短码），
 * 列表返回后由 completePendingCountry 翻译为站内 {regionId} / {regionId}other 形态。
 */
const REGION_ALIAS_PATTERN = /^[A-Za-z]{3,}$/;

const isSameAlias = (alias: string, candidate?: string | null) => Boolean(candidate && candidate.toLowerCase() === alias.toLowerCase());

/**
 * 站内大区 countryCode（{regionId} / {regionId}other）反查为短码，供写地址栏使用（RES-3316）；
 * 非大区形态、大区列表未就绪或后端未配置短码时原样返回，即维持写 id 的现状。
 */
const toUrlCountryCode = (countryCode: string, regionCountryList: IConvertedRegionItem[]): string => {
  const regionMatch = REGION_CODE_PATTERN.exec(countryCode);
  if (!regionMatch) return countryCode;
  const region = regionCountryList.find((item) => String(item.id) === regionMatch[1]);
  const alias = regionMatch[2] ? region?.restCode : region?.regionCode;
  return alias || countryCode;
};

/** 是否为站点支持的语言（config.languageList 是语言总表，管不了国别——国别口径见 resolveCountryLanguage） */
export const isSupportedLanguage = (lang?: string | null): lang is string => Boolean(lang && config.languageList.some((item) => item.code === lang));

/**
 * URL 显式指定且站点支持的语言（?language=），无 / 非法为 null。
 * 只在首次解析时捕获：syncCountryToUrl 之后地址栏上的 language 是站内自己写回去的，
 * 再读就分不清哪个是用户点名要的语言。
 */
let urlPreferredLanguage: string | null | undefined;

/**
 * 占位国家（name 为空）补全时确定语言：URL 点名的语言该国支持就用它，否则用该国第一门语言；
 * 大区 / 大区 Other 没有国家语言清单，固定站点默认（英文）——口径与国家选择面板给的可选项一致。
 * 已有名字的国家直接沿用当前语言：语言切换会重拉大区列表、触发大区名二次补全，
 * 此时改语言会把用户在面板里选定的「国家 + 语言」组合冲回默认。
 */
const resolveCountryLanguage = (country: ICountry, languageList?: ILanguageConfig[]): string => {
  if (country.name) return country.language;
  const codes = languageList?.map((item) => item.code) ?? [];
  if (!codes.length) return config.defaultLanguage;
  return (urlPreferredLanguage && codes.includes(urlPreferredLanguage) ? urlPreferredLanguage : codes[0]) || country.language;
};

/** 是否为大区（All Regions）/ 大区 Other 形态的 countryCode */
export const isRegionCountryCode = (code: string): boolean => REGION_CODE_PATTERN.test(code);

/**
 * 是否为 Global 站的 countryCode（大小写不敏感，容忍手拼 ?country=Global）。
 * 站内生成的值统一是小写 'global'，但 URL 参数与外部入口都可能给到别的大小写。
 */
export const isGlobalCountryCode = (code?: string | null): boolean => code?.toLowerCase() === config.defaultCountryInfo.countryCode;

/**
 * 从 URL 解析国家参数，返回按参数生效的国家上下文。
 * 取值优先级：URL 参数 > 本地已选国家 > 站点默认推导（PRD F3）。
 * 无参数 / 国家码非法时返回 null，由调用方回退现状逻辑（存量链接行为不变）。
 * 语言的可选范围由国家决定（见 SelectCountry），非法语言只回落语言、不影响国家生效：
 * Global / 大区 / 大区 Other 固定英文；具体国家的语言清单在大区列表里，boot 时拿不到，
 * 先按 URL 值占位，由 completePendingCountry 用该国清单校正。
 */
export const resolveCountryFromUrl = (): ICountry | null => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get(COUNTRY_URL_PARAM)?.trim();
  if (!code) return null;

  const lang = params.get(LANG_URL_PARAM)?.trim();
  const preferredLang = isSupportedLanguage(lang) ? lang : undefined;
  if (urlPreferredLanguage === undefined) urlPreferredLanguage = preferredLang ?? null;

  // Global 站点（大小写不敏感，容忍手拼 ?country=Global）：只有英文，不吃 language 参数
  if (isGlobalCountryCode(code)) {
    return { ...config.defaultCountryInfo };
  }

  // 国家取值口径以后端大区列表为唯一权威——静态 config.countryList 已与线上脱节（缺 FR/LU/SK 等
  // 已开通国家，又含 CN/JP/SG 等已下线国家），不能用作校验源。boot 时大区列表尚未拉取，
  // 格式合法（ISO 国家码 / 大区 / 大区 Other / 大区短码）即按占位放行：接口传参用 code 即可正确渲染内容，
  // 展示名补全与最终合法性校验在大区列表返回后由 completePendingCountry 收口，查不到才回退默认（PRD F4）
  if (REGION_CODE_PATTERN.test(code) || ISO_CODE_PATTERN.test(code) || REGION_ALIAS_PATTERN.test(code)) {
    // ISO 码归一为大写、大区/Other 码归一为小写，与站内生成的 countryCode 形态一致（国家面板 active 判断按原值比较）；
    // 大区短码保留原值（服务端大小写不敏感，站内形态由 completePendingCountry 翻译后统一）
    const normalizedCode = ISO_CODE_PATTERN.test(code) ? code.toUpperCase() : REGION_CODE_PATTERN.test(code) ? code.toLowerCase() : code;
    // 只有具体国家才可能是英文之外的语言，且清单要等大区列表；大区形态直接落英文
    const placeholderLang = (ISO_CODE_PATTERN.test(code) ? preferredLang : undefined) ?? config.defaultLanguage;
    return { continentName: '', name: '', countryCode: normalizedCode, language: placeholderLang };
  }

  return null;
};

let appliedUrlCountry: ICountry | null | undefined;

/**
 * 应用 URL 国家/语言参数到本地缓存（幂等，整个应用生命周期只解析并落地一次）。
 * 必须在 @/i18n 模块初始化之前执行——i18n boot 读 localStorage 语言，若此时还是旧语言，
 * 会先拉一份旧语言翻译、Init 对齐时再拉新语言的第二份（双份串行加载 + 二次 Suspense，
 * 显著拖慢带参落地的页面进入）。@/i18n 与 store/country 均调用本函数，谁先执行都能保证时序。
 * 返回按参数生效的国家（URL 与缓存一致时返回缓存对象，其后端来源的展示信息更完整）；
 * 无参数 / 非法时返回 null，不触碰缓存（PRD F4）。
 */
export const applyUrlCountry = (): ICountry | null => {
  if (appliedUrlCountry !== undefined) return appliedUrlCountry;
  const urlCountry = resolveCountryFromUrl();
  if (!urlCountry) {
    appliedUrlCountry = null;
    return appliedUrlCountry;
  }
  const local = getLocalCountryInfo();
  if (urlCountry.countryCode === local.countryCode && urlCountry.language === local.language) {
    appliedUrlCountry = local;
    return appliedUrlCountry;
  }
  setLocalCountryInfo(urlCountry);
  setLocalLanguage(urlCountry.language);
  appliedUrlCountry = urlCountry;
  return appliedUrlCountry;
};

/**
 * URL 占位国家在大区列表中查不到（格式合法但未开通 / 拼错）时的回退目标：一律站点默认 Global。
 * 带参链接按「新打开」处理，不继承本机上一次选过的国家——同一条链接换台机器打开结果才一致。
 */
export const getInvalidUrlCountryFallback = (): ICountry => ({ ...config.defaultCountryInfo });

/**
 * URL 占位国家的展示信息补全：在后端大区列表（国家取值的唯一权威）中找到对应国家或大区则补全名称
 * （name 用 countryEnglishName，与国家选择面板的存值口径一致），并按该国清单校正语言（resolveCountryLanguage）；
 * 找不到视为非法参数返回 null，由调用方回退站点默认 Global。
 */
export const completePendingCountry = (country: ICountry, regionCountryList: IConvertedRegionItem[], otherName: string): ICountry | null => {
  // 大区短码别名（RES-3316）：翻译为站内 {regionId} / {regionId}other 形态，国家面板 active 判断等下游消费方零改动
  if (REGION_ALIAS_PATTERN.test(country.countryCode)) {
    for (const region of regionCountryList) {
      if (isSameAlias(country.countryCode, region.regionCode)) {
        return {
          ...country,
          countryCode: String(region.id),
          continentName: region.regionName,
          name: region.regionName,
          isGlobalDefault: region.isGlobalDefault,
          language: resolveCountryLanguage(country),
        };
      }
      if (isSameAlias(country.countryCode, region.restCode)) {
        return {
          ...country,
          countryCode: `${region.id}other`,
          continentName: region.regionName,
          name: otherName,
          isGlobalDefault: region.isGlobalDefault,
          language: resolveCountryLanguage(country),
        };
      }
    }
    return null;
  }
  const regionMatch = REGION_CODE_PATTERN.exec(country.countryCode);
  if (regionMatch) {
    const region = regionCountryList.find((item) => String(item.id) === regionMatch[1]);
    if (!region) return null;
    return {
      ...country,
      continentName: region.regionName,
      name: regionMatch[2] ? otherName : region.regionName,
      isGlobalDefault: region.isGlobalDefault,
      language: resolveCountryLanguage(country),
    };
  }
  for (const region of regionCountryList) {
    const match = region.countryList?.find((item) => item.countryCode === country.countryCode);
    if (match) {
      return {
        ...country,
        continentName: region.regionName,
        name: match.countryEnglishName,
        isGlobalDefault: region.isGlobalDefault,
        language: resolveCountryLanguage(country, match.languageList),
      };
    }
  }
  return null;
};

/**
 * 启动时按大区列表（国家取值的唯一权威）敲定最终的国家与语言：
 * 校验只用 countryCode / languageList / 大区 id 与短码，这些字段与请求语言无关，
 * 所以大区列表用哪种语言拉回来都不影响结论（受语言影响的只有大区展示名 regionName）。
 * 展示名不在这里补——那要用 country 命名空间的 other 文案，而此时 i18n 还没启动，
 * 仍留给 Layout 收口，所以返回值只取校验后的 countryCode 与 language。
 * 列表不可用时原样返回，维持现状而不是把用户选的国家冲掉。
 */
export const resolveBootCountry = (country: ICountry, regionCountryList: IConvertedRegionItem[]): ICountry => {
  if (!regionCountryList.length) return country;
  const completed = completePendingCountry(country, regionCountryList, '');
  if (completed) return { ...country, countryCode: completed.countryCode, language: completed.language };
  // 列表中查不到：URL 占位（空名）视为非法参数回退默认；已有名字的历史选择不动，
  // 避免后端临时少返一个国家就把用户选过的国家冲掉
  return country.name ? country : getInvalidUrlCountryFallback();
};

/**
 * 把当前生效国家/语言同步到地址栏（replaceState：不产生历史记录、不触发路由），
 * 使任何时刻复制地址栏链接都对应当前所见页面（PRD F1/F2）。
 * 大区 / 大区 Other 优先写可读短码（RES-3316，需传入大区列表反查），查不到短码时维持写 id。
 */
export const syncCountryToUrl = (country: ICountry, regionCountryList: IConvertedRegionItem[] = []) => {
  const { language } = country;
  if (!country.countryCode) return;
  const countryCode = toUrlCountryCode(country.countryCode, regionCountryList);
  const url = new URL(window.location.href);
  // language 运行时可能为空（如后端国家的 languageList 为空数组时 findCountryByCode 取不到值），
  // 判空跳过、保留 URL 现值，避免把字符串 "undefined" 写进地址栏
  const nextLanguage = language || url.searchParams.get(LANG_URL_PARAM);
  if (url.searchParams.get(COUNTRY_URL_PARAM) === countryCode && url.searchParams.get(LANG_URL_PARAM) === nextLanguage) {
    return;
  }
  url.searchParams.set(COUNTRY_URL_PARAM, countryCode);
  if (nextLanguage) {
    url.searchParams.set(LANG_URL_PARAM, nextLanguage);
  }
  window.history.replaceState(window.history.state, '', url);
};
